"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTrackBoundaryError = exports.useOverflowTrimList = exports.useMediaQuery = exports.useProfileSections = void 0;
const react_1 = __importStar(require("react"));
const tooltip_1 = __importDefault(require("@kyruus/tooltip"));
const styles_1 = require("../../components/ProviderHighlights/styles");
const utils_1 = require("../utils");
const messages_1 = __importDefault(require("../messages"));
const contexts_1 = require("../contexts");
const constants_1 = require("../constants");
var useProfileSections_1 = require("./useProfileSections");
Object.defineProperty(exports, "useProfileSections", { enumerable: true, get: function () { return useProfileSections_1.useProfileSections; } });
/**
 * Checks if a given media query matches (https://usehooks-ts.com/react-hook/use-media-query)
 * useMediaQuery('(min-width: 768px)')
 * @param {string} `query` the media query to match
 * @returns {boolean}
 */
const useMediaQuery = (query) => {
    const getMatches = (query) => {
        // Prevents SSR issues
        if (typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function') {
            return window.matchMedia(query).matches;
        }
        return false;
    };
    const [matches, setMatches] = (0, react_1.useState)(getMatches(query));
    function handleChange() {
        setMatches(getMatches(query));
    }
    (0, react_1.useEffect)(() => {
        const matchMedia = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
            ? window.matchMedia(query)
            : null;
        if (matchMedia === null) {
            return;
        }
        // Triggered at the first client-side load and if query changes
        handleChange();
        // Listen matchMedia
        if (matchMedia.addListener) {
            matchMedia.addListener(handleChange);
        }
        else {
            matchMedia.addEventListener('change', handleChange);
        }
        return () => {
            if (matchMedia.removeListener) {
                matchMedia.removeListener(handleChange);
            }
            else {
                matchMedia.removeEventListener('change', handleChange);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);
    return matches;
};
exports.useMediaQuery = useMediaQuery;
/**
 * Trims a list of information card items down to fit within the card container
 * @param {string[]} `items` the items to trim
 * @param {React.ReactNode} `filterMatchClearButton` the button to clear a filter match icon (for inclusion in the list of items)
 * @param {IntlShape} `intl` the IntlShape object from react-intl
 * @returns { checkMarkIcon: React.ReactElement | null, filterMatchClearButton: React.ReactElement | null }
 */
const useOverflowTrimList = (items, intl) => {
    const totalCount = Array.isArray(items) ? items.length : 0;
    const [renderCount, setRenderCount] = (0, react_1.useState)(totalCount);
    const [windowWidth, setWindowWidth] = (0, react_1.useState)(0);
    const listRef = (0, react_1.useRef)(null);
    const isMountedRef = (0, react_1.useRef)(true);
    // track component mounted state so that debounce resize handler doesn't fire when component is unmounted
    (0, react_1.useLayoutEffect)(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);
    // decrement the count of rendered items until width of <ul> is within width of container
    (0, react_1.useLayoutEffect)(() => {
        if (listRef.current) {
            const { clientWidth, scrollWidth } = listRef.current;
            if (scrollWidth > clientWidth) {
                setRenderCount((current) => current - 1);
            }
        }
    }, [renderCount, windowWidth]);
    // add window size listener
    (0, react_1.useEffect)(() => {
        const resizeListener = (0, utils_1.debounce)(() => {
            if (isMountedRef.current) {
                // reset render count to kick off another trim
                setRenderCount(totalCount);
                setWindowWidth(window.innerWidth);
            }
        }, 300);
        setWindowWidth(window.innerWidth);
        window.addEventListener('resize', resizeListener);
        return () => {
            window.removeEventListener('resize', resizeListener);
        };
    }, [totalCount]);
    // reset render count if the items list size changes
    (0, react_1.useEffect)(() => {
        setRenderCount(totalCount);
    }, [totalCount]);
    // include the filter match clear button in the render items
    const trimmedItems = Array.isArray(items) ? items.slice(0, renderCount) : [];
    const renderItems = trimmedItems;
    const hiddenItems = Array.isArray(items)
        ? items.slice(renderCount, totalCount)
        : [];
    const allItemsHidden = hiddenItems.length === totalCount;
    const moreTooltip = (react_1.default.createElement(tooltip_1.default, { title: hiddenItems.join(', '), "data-testid": "more-tooltip" },
        react_1.default.createElement(styles_1.MoreTooltipContent, { tabIndex: 0, "aria-label": `${intl.formatMessage(messages_1.default.ariaMoreTooltipText, {
                itemLength: hiddenItems.length,
                items: hiddenItems.join(', ')
            })}` }, `${intl.formatMessage(messages_1.default.informationCardMoreTooltipText, {
            itemsCount: hiddenItems.length,
            tooltipText: allItemsHidden
                ? intl.formatMessage(messages_1.default.informationCardTooltipTextItems)
                : intl.formatMessage(messages_1.default.informationCardTooltipTextMore)
        })}`)));
    const renderItemsWithTooltip = [...renderItems, moreTooltip];
    return {
        listRef,
        renderItems: hiddenItems.length ? renderItemsWithTooltip : renderItems
    };
};
exports.useOverflowTrimList = useOverflowTrimList;
const useTrackBoundaryError = (component) => {
    const trackingFunction = (0, react_1.useContext)(contexts_1.TrackingFunctionContext);
    return (error, info) => {
        trackingFunction(constants_1.TRACKING_EVENTS.BOUNDARY_ERROR, {
            component,
            error: error.toString(),
            info
        });
    };
};
exports.useTrackBoundaryError = useTrackBoundaryError;
//# sourceMappingURL=index.js.map